import { useMemo, useState } from "react";
import { useRuleSetNames } from "../../../hooks/useRuleSetNames";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Snackbar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  useAutoTransactionRules,
  useDeleteAutoTransactionRule,
  useUpdateAutoTransactionRule,
} from "../../../api/queries";
import type { AutoTransactionRuleDto, AutoTxId } from "../../../api/types";
import { ApiError } from "../../../api/common";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import {
  AdminListBody,
  AdminListPageHeader,
} from "../../../components/AdminList";
import {
  SortByDropdown,
  type SortByOption,
} from "../../../components/SortByDropdown";
import {
  ViewModeToggle,
  type ViewMode,
} from "../../../components/ViewModeToggle";
import { AutoTransactionFilters } from "./components/AutoTransactionFilters";
import { AutoTransactionListView } from "./components/AutoTransactionListView";
import { AutoTransactionTileView } from "./components/AutoTransactionTileView";
import { BulkActionBar } from "./components/BulkActionBar";
import { RuleAppliedModal } from "./components/RuleAppliedModal";
import { AutoTransactionMatchesDialog } from "../../../components/AutoTransactionMatchesDialog";
import {
  defaultFilters,
  isDefaultFilters,
  type Filters,
  type RuleSort,
} from "./types";
import { filterRules, sortRules } from "./filterRules";

const SORT_BY_KEY = "autoTransactions.sortBy";
const VIEW_MODE_KEY = "autoTransactions.viewMode";

const readStoredViewMode = (): ViewMode => {
  try {
    const stored = window.localStorage?.getItem?.(VIEW_MODE_KEY);
    return stored === "tile" ? "tile" : "list";
  } catch {
    return "list";
  }
};

const writeStoredViewMode = (next: ViewMode) => {
  try {
    window.localStorage?.setItem?.(VIEW_MODE_KEY, next);
  } catch {
    // ignore — SSR, private mode, or test env without storage
  }
};

const SORT_OPTIONS: ReadonlyArray<SortByOption<RuleSort>> = [
  { value: "priorityDesc", label: "Priority (high → low)" },
  { value: "priorityAsc", label: "Priority (low → high)" },
  { value: "nameAsc", label: "Name" },
];

const isRuleSort = (s: string | null): s is RuleSort =>
  s === "priorityDesc" || s === "priorityAsc" || s === "nameAsc";

const readStoredSortBy = (): RuleSort => {
  try {
    const stored = window.localStorage?.getItem?.(SORT_BY_KEY);
    return isRuleSort(stored) ? stored : "priorityDesc";
  } catch {
    return "priorityDesc";
  }
};

const writeStoredSortBy = (next: RuleSort) => {
  try {
    window.localStorage?.setItem?.(SORT_BY_KEY, next);
  } catch {
    // ignore — SSR, private mode, or test env without storage
  }
};

export const AutoTransactionsPage = () => {
  const navigate = useNavigate();
  const { data: rules, isPending } = useAutoTransactionRules();
  const deleteRule = useDeleteAutoTransactionRule();
  const updateRule = useUpdateAutoTransactionRule();

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortBy, setSortBy] = useState<RuleSort>(() => readStoredSortBy());
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    readStoredViewMode(),
  );

  const [confirmDeleteRule, setConfirmDeleteRule] =
    useState<AutoTransactionRuleDto | null>(null);
  const [inUseRule, setInUseRule] = useState<{
    id: AutoTxId;
    name: string;
  } | null>(null);
  // Two questions, two dialogs: what the condition matches now, and what the
  // rule has actually been applied to.
  const [previewRule, setPreviewRule] = useState<AutoTransactionRuleDto | null>(
    null,
  );
  const [appliedRule, setAppliedRule] = useState<AutoTransactionRuleDto | null>(
    null,
  );

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Page-level expand state so per-row open state survives parent re-renders
  // (e.g. React Query refetches, drag operations, sort changes).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSortByChange = (next: RuleSort) => {
    setSortBy(next);
    writeStoredSortBy(next);
  };

  const handleViewModeChange = (next: ViewMode) => {
    setViewMode(next);
    writeStoredViewMode(next);
  };
  const setNames = useRuleSetNames();

  const visibleRules = useMemo(() => {
    if (!rules) return [];
    // Pass the set-name resolvers so searching for a shared set's name finds
    // the rules citing it — a reference carries only an id otherwise.
    return sortRules(
      filterRules(rules, filters, {
        matchSet: setNames.matchSet,
        actionSet: setNames.actionSet,
      }),
      sortBy,
    );
  }, [rules, filters, sortBy, setNames]);

  const total = rules?.length ?? 0;

  const handleConfirmDelete = async () => {
    if (!confirmDeleteRule) return;
    const rule = confirmDeleteRule;
    setConfirmDeleteRule(null);
    try {
      await deleteRule.mutateAsync(rule.id);
      setSnackbar({
        severity: "success",
        message: `Deleted rule "${rule.name}".`,
      });
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.data.type === "autoTransactionInUse"
      ) {
        setInUseRule({ id: error.data.ruleId as AutoTxId, name: rule.name });
      } else {
        setSnackbar({ severity: "error", message: "Failed to delete rule." });
      }
    }
  };

  const handleToggleEnabled = (rule: AutoTransactionRuleDto, next: boolean) => {
    setTogglingId(rule.id);
    updateRule.mutate(
      {
        id: rule.id,
        rule: {
          name: rule.name,
          description: rule.description,
          priority: rule.priority,
          condition: rule.condition,
          actions: rule.actions,
          enabled: next,
        },
      },
      {
        onError: () =>
          setSnackbar({
            severity: "error",
            message: `Failed to ${next ? "enable" : "disable"} "${rule.name}".`,
          }),
        onSettled: () => setTogglingId(null),
      },
    );
  };

  const handleDuplicate = (rule: AutoTransactionRuleDto) => {
    navigate("/admin/auto-transactions/new", {
      state: {
        fromRule: {
          ...rule,
          name: `Copy of ${rule.name}`,
        },
      },
    });
  };

  const toggleSelection = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const applyBulkEnabled = async (next: boolean) => {
    if (!rules || selectedIds.size === 0) return;
    const targets = rules.filter(
      (r) => selectedIds.has(r.id) && r.enabled !== next && r.editable,
    );
    if (targets.length === 0) {
      exitSelectionMode();
      setSnackbar({
        severity: "success",
        message: `Already ${next ? "enabled" : "disabled"}.`,
      });
      return;
    }
    setBulkBusy(true);
    const results = await Promise.allSettled(
      targets.map((r) =>
        updateRule.mutateAsync({
          id: r.id,
          rule: {
            name: r.name,
            description: r.description,
            priority: r.priority,
            condition: r.condition,
            actions: r.actions,
            enabled: next,
          },
        }),
      ),
    );
    setBulkBusy(false);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    setSnackbar({
      severity: failed === 0 ? "success" : "error",
      message:
        failed === 0
          ? `${next ? "Enabled" : "Disabled"} ${ok} rule${ok === 1 ? "" : "s"}.`
          : `${next ? "Enabled" : "Disabled"} ${ok} rule${ok === 1 ? "" : "s"}, ${failed} failed.`,
    });
    exitSelectionMode();
  };

  const selection = selectionMode
    ? { selectedIds, onToggle: toggleSelection }
    : null;

  // Drag-to-reorder is only meaningful when the visible list reflects the
  // canonical priority order. We require: priority-desc sort, no filters
  // narrowing the list, list view, and not in selection mode.
  const dragEnabled =
    viewMode === "list" &&
    sortBy === "priorityDesc" &&
    !selectionMode &&
    isDefaultFilters(filters);

  /**
   * Repacks priorities to descending integers spaced by 10 (so future inserts
   * have room) and PUTs only those whose priority actually changed.
   */
  const handleReorder = async (orderedIds: string[]) => {
    if (!rules) return;
    const byId = new Map<string, AutoTransactionRuleDto>(
      rules.map((r) => [r.id as string, r]),
    );
    // Only editable (stored) rules carry a real priority. Virtual invoice rules
    // aren't persisted, so exclude them — reordering them would PUT a
    // non-existent id.
    const orderedRules = orderedIds
      .map((id) => byId.get(id))
      .filter((r): r is AutoTransactionRuleDto => !!r && r.editable);
    const n = orderedRules.length;
    const changed = orderedRules
      .map((r, index) => ({ rule: r, nextPriority: (n - index) * 10 }))
      .filter(({ rule, nextPriority }) => rule.priority !== nextPriority);
    if (changed.length === 0) return;

    const results = await Promise.allSettled(
      changed.map(({ rule, nextPriority }) =>
        updateRule.mutateAsync({
          id: rule.id,
          rule: {
            name: rule.name,
            description: rule.description,
            priority: nextPriority,
            condition: rule.condition,
            actions: rule.actions,
            enabled: rule.enabled,
          },
        }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      setSnackbar({
        severity: "error",
        message: `Reorder failed for ${failed} rule${failed === 1 ? "" : "s"}.`,
      });
    }
  };

  const dragHandlers = dragEnabled ? { onReorder: handleReorder } : null;

  return (
    <Box sx={{ p: 2 }}>
      <AdminListPageHeader
        title="Auto transactions"
        actions={
          <>
            <SortByDropdown
              value={sortBy}
              onChange={handleSortByChange}
              options={SORT_OPTIONS}
              labelId="auto-tx-sort-by"
            />
            <ViewModeToggle
              value={viewMode}
              onChange={handleViewModeChange}
              ariaLabel="auto transactions view mode"
            />
            <Button
              size="small"
              variant={selectionMode ? "contained" : "outlined"}
              startIcon={
                selectionMode ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />
              }
              onClick={() =>
                selectionMode ? exitSelectionMode() : setSelectionMode(true)
              }
            >
              {selectionMode ? "Done" : "Select"}
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => navigate("/admin/auto-transactions/new")}
            >
              New rule
            </Button>
          </>
        }
      />

      <AutoTransactionFilters value={filters} onChange={setFilters} />

      <AdminListBody
        isLoading={isPending}
        total={total}
        filteredCount={visibleRules.length}
        noun="rule"
      >
        {viewMode === "list" ? (
          <AutoTransactionListView
            rules={visibleRules}
            togglingId={togglingId}
            selection={selection}
            dragHandlers={dragHandlers}
            expandedIds={expandedIds}
            onToggleExpanded={toggleExpanded}
            onEdit={(rule) => navigate(`/admin/auto-transactions/${rule.id}`)}
            onDuplicate={handleDuplicate}
            onPreviewMatches={setPreviewRule}
            onShowApplied={setAppliedRule}
            onDelete={setConfirmDeleteRule}
            onToggleEnabled={handleToggleEnabled}
          />
        ) : (
          <AutoTransactionTileView
            rules={visibleRules}
            togglingId={togglingId}
            selection={selection}
            onEdit={(rule) => navigate(`/admin/auto-transactions/${rule.id}`)}
            onDuplicate={handleDuplicate}
            onPreviewMatches={setPreviewRule}
            onShowApplied={setAppliedRule}
            onDelete={setConfirmDeleteRule}
            onToggleEnabled={handleToggleEnabled}
          />
        )}
      </AdminListBody>

      {selectionMode && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          isBusy={bulkBusy}
          onEnable={() => applyBulkEnabled(true)}
          onDisable={() => applyBulkEnabled(false)}
          onClear={() => setSelectedIds(new Set())}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteRule}
        title="Delete Rule"
        message={
          confirmDeleteRule
            ? `Are you sure you want to delete the rule "${confirmDeleteRule.name}"?`
            : ""
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteRule(null)}
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Keyed so reopening on a different rule refetches rather than showing
          the previous rule's matches while the new ones load. */}
      {previewRule && (
        <AutoTransactionMatchesDialog
          key={previewRule.id}
          open
          condition={previewRule.condition}
          onClose={() => setPreviewRule(null)}
        />
      )}

      <RuleAppliedModal
        open={!!appliedRule}
        ruleId={appliedRule?.id ?? null}
        ruleName={appliedRule?.name}
        onClose={() => setAppliedRule(null)}
      />

      <RuleAppliedModal
        open={!!inUseRule}
        ruleId={inUseRule?.id ?? null}
        ruleName={inUseRule?.name}
        intro="This auto transaction rule is currently in use by the following transactions. You must remove the rule from these transactions before you can delete it."
        onClose={() => setInUseRule(null)}
      />

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar?.severity ?? "info"}
          onClose={() => setSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
